import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Car, 
  Clock, 
  CheckCircle,
  Calendar,
  TrendingUp,
  Loader2,
  AlertCircle,
  Info,
  Plus,
  MapPin,
  User,
  Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ConciergeDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/concierge/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      available: 'bg-green-100 text-green-800',
      claimed: 'bg-blue-100 text-blue-800',
      assigned: 'bg-gold-100 text-gold-800',
      completed: 'bg-gray-100 text-gray-800',
      picked_up: 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const rides = dashboardData?.rides || [];
  const upcomingRides = dashboardData?.upcoming_rides || [];

  return (
    <div className="space-y-6" data-testid="concierge-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Concierge Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button onClick={() => navigate('/concierge/new-ride')} data-testid="new-ride-btn">
          <Plus className="h-4 w-4 mr-2" />
          Post New Ride
        </Button>
      </div>

      {/* Commission Disclaimer */}
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-800 font-medium">Commission Structure</p>
              <p className="text-sm text-purple-700 mt-1">
                You earn <strong>20% commission</strong> on every ride you post that gets completed by a driver. 
                Drivers receive 80% of the ride total. Payouts are processed <strong>every Monday</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Car className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_rides_posted || 0}</p>
                <p className="text-xs text-gray-500">Rides Posted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gold-100 rounded-lg">
                <Clock className="h-5 w-5 text-gold-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending_rides || 0}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed_rides || 0}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_commission_earned)}</p>
                <p className="text-xs text-gray-500">Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Commission Card */}
      {stats.pending_commission > 0 && (
        <Card className="border-gold-200 bg-gold-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-gold-600" />
                <div>
                  <p className="font-medium text-gold-800">Pending Commission</p>
                  <p className="text-sm text-gold-700">From rides awaiting completion</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-gold-800">{formatCurrency(stats.pending_commission)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Rides */}
      {upcomingRides.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Upcoming Rides
            </CardTitle>
            <CardDescription>Rides you've posted that are scheduled soon</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingRides.slice(0, 5).map(ride => (
                <div key={ride.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{ride.client_name}</span>
                      <Badge className={getStatusBadge(ride.status)}>{ride.status}</Badge>
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {ride.pickup_location?.substring(0, 40)}...
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDate(ride.date)}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-600">{formatCurrency(ride.concierge_commission)}</p>
                    <p className="text-xs text-gray-400">Your commission</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Rides */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Rides</CardTitle>
          <CardDescription>All rides you've posted</CardDescription>
        </CardHeader>
        <CardContent>
          {rides.length === 0 ? (
            <div className="py-8 text-center">
              <Car className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No rides posted yet</p>
              <Button 
                className="mt-4" 
                onClick={() => navigate('/concierge/new-ride')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Post Your First Ride
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rides.map(ride => (
                <div key={ride.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{ride.client_name}</span>
                        <Badge className={getStatusBadge(ride.status)}>{ride.status}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-green-500" />
                          <span className="truncate">{ride.pickup_location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-red-500" />
                          <span className="truncate">{ride.dropoff_location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>{formatDate(ride.date)}</span>
                        {ride.client_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {ride.client_phone}
                          </span>
                        )}
                      </div>
                      {ride.driver_info && (
                        <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                          Driver: {ride.driver_info.name}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{formatCurrency(ride.total_amount)}</p>
                      <p className="text-sm text-purple-600 font-medium">
                        +{formatCurrency(ride.concierge_commission)} commission
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Info */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Payout Schedule</p>
              <p className="text-sm text-green-700">
                Commissions are processed and paid out <strong>every Monday</strong>. 
                Contact admin for payout method setup.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConciergeDashboard;
